import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Search } from 'lucide-react';
import { fetchCustomersList } from '../lib/bridge';
import { LoadingState } from '../components/ui/LoadingState';
import { ErrorState } from '../components/ui/ErrorState';
import { EmptyState } from '../components/ui/EmptyState';
import { PageHeader } from '../components/ui/PageHeader';
import { Badge } from '../components/ui/Badge';
import { fieldControlClass } from '../components/ui/forms';

export function CustomersPage() {
  const [search, setSearch] = useState('');
  const query = useQuery({ queryKey: ['customers', search], queryFn: () => fetchCustomersList(search) });

  return (
    <div className="space-y-4">
      <PageHeader eyebrow="Directory" title="Customers" />
      <div className="relative max-w-sm">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" aria-hidden="true" />
        <input className={`${fieldControlClass} pl-9`} placeholder="Search customers…" value={search} onChange={(e) => setSearch(e.target.value)} />
      </div>

      {query.isLoading && <LoadingState label="Loading customers…" />}
      {query.isError && (
        <ErrorState message={query.error instanceof Error ? query.error.message : 'Could not load customers.'} onRetry={() => query.refetch()} />
      )}
      {query.data && !query.data.ok && <ErrorState message={query.data.message || 'Could not load customers.'} onRetry={() => query.refetch()} />}
      {query.data?.ok && (query.data.customers?.length ?? 0) === 0 && <EmptyState>No customers found.</EmptyState>}
      {query.data?.ok && (query.data.customers?.length ?? 0) > 0 && (
        <ul className="grid gap-2 sm:grid-cols-2 desktop:grid-cols-3">
          {query.data.customers!.map((c) => (
            <li key={c.customerId}>
              <Link
                to={`/customers/${encodeURIComponent(c.customerId)}`}
                className="flex items-center justify-between gap-3 rounded-xl border border-ink-border bg-panel-gradient p-4 shadow-card transition hover:border-gold-700/60"
              >
                <div className="min-w-0">
                  <p className="truncate font-semibold text-white">{c.name}</p>
                  <p className="truncate text-sm text-slate-400">{c.phone || c.email || c.address || '—'}</p>
                </div>
                {typeof c.jobCount === 'number' && (
                  <Badge tone="neutral" className="shrink-0">
                    {c.jobCount} job{c.jobCount === 1 ? '' : 's'}
                  </Badge>
                )}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
